<template>
  <div class="task-stepper-host">
    <!-- MAIN VIEW: the 3-step task editor -->
    <template v-if="view === 'stepper'">
      <!-- Vertical (collapsed) stepper on phones; horizontal headers are kept
           compact (icon + title, no captions) so three steps fit the dialog -->
      <q-stepper
        ref="stepper"
        v-model="step"
        flat
        animated
        keep-alive
        :vertical="$q.screen.xs"
        active-color="primary"
        done-color="green-7"
        class="task-stepper editor-scroll"
      >
        <!-- STEP 1: The task itself -->
        <q-step
          :name="1"
          title="Task"
          icon="mdi-format-list-checks"
          :done="step > 1"
        >
          <q-item-wrapper
            label="Task"
            label-class="text-weight-medium"
            required
            class="q-px-sm"
          >
            <q-input
              ref="textInput"
              v-model="form.text"
              dense
              outlined
              autofocus
              hide-bottom-space
              placeholder="What needs doing?"
              :error="showTextError"
              error-message="Enter some text for this task"
              @keyup.enter="next"
            />
          </q-item-wrapper>

          <q-item
            tag="label"
            class="q-mt-md q-px-sm"
          >
            <q-item-section side top>
              <q-toggle v-model="form.is_required" dense />
            </q-item-section>
            <q-item-section>
              <q-item-label>Required task</q-item-label>
              <q-item-label caption>
                Required tasks count towards the appointment's progress and block marking the
                appointment as complete when "Lock Incomplete Status" is enabled in System Settings
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-step>

        <!-- STEP 2: Requirements -->
        <q-step
          :name="2"
          title="Requirements"
          icon="mdi-clipboard-check-outline"
          :done="step > 2"
        >
          <q-item-label caption class="q-mb-sm q-px-sm">
            Optionally attach requirements. The task can only be ticked off once every
            requirement below is satisfied — and it ticks itself off automatically if
            auto-complete is enabled in System Settings.
          </q-item-label>

          <q-list>
            <q-item tag="label" class="q-px-sm">
              <q-item-section side top>
                <q-toggle v-model="form.requires_photo" dense />
              </q-item-section>
              <q-item-section avatar class="requirement-icon">
                <q-icon name="mdi-camera" color="blue-grey-7" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Photo</q-item-label>
                <q-item-label caption>
                  The task can be ticked off once at least one photo is attached to it.
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item tag="label" class="q-px-sm">
              <q-item-section side top>
                <q-toggle v-model="form.requires_invoice" dense />
              </q-item-section>
              <q-item-section avatar class="requirement-icon">
                <q-icon name="mdi-file-document-outline" color="blue-grey-7" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Invoice</q-item-label>
                <q-item-label caption>
                  {{ invoiceExplanation }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item tag="label" class="q-px-sm">
              <q-item-section side top>
                <q-toggle v-model="form.requires_quotation" dense />
              </q-item-section>
              <q-item-section avatar class="requirement-icon">
                <q-icon name="mdi-file-document-edit-outline" color="blue-grey-7" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Quote</q-item-label>
                <q-item-label caption>
                  {{ quotationExplanation }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item tag="label" class="q-px-sm">
              <q-item-section side top>
                <q-toggle
                  v-model="requiresForm"
                  dense
                  @update:model-value="onToggleForm"
                />
              </q-item-section>
              <q-item-section avatar class="requirement-icon">
                <q-icon name="mdi-clipboard-text-outline" color="blue-grey-7" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Report</q-item-label>
                <q-item-label caption>
                  {{ reportExplanation }}
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>

          <!-- Management panel for the linked report templates -->
          <q-card
            v-if="requiresForm"
            flat
            bordered
            class="q-mt-xs q-mx-sm"
          >
            <q-list separator dense>
              <q-item
                v-for="f in form.required_forms"
                :key="f.id"
              >
                <q-item-section avatar class="requirement-icon">
                  <q-icon name="mdi-clipboard-text-outline" color="blue-grey-7" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ f.title }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <cancel-button
                    flat
                    @click="removeForm(f.id)"
                  />
                </q-item-section>
              </q-item>

              <q-item v-if="form.required_forms.length === 0">
                <q-item-section>
                  <q-item-label caption>
                    No report templates linked yet
                  </q-item-label>
                </q-item-section>
              </q-item>

              <clickable-item
                label="Add report template..."
                @click="openFormPicker"
              />
            </q-list>
          </q-card>
        </q-step>

        <!-- STEP 3: Review -->
        <q-step
          :name="3"
          title="Review"
          icon="mdi-check-circle-outline"
        >
          <q-item class="q-px-sm">
            <q-item-section>
              <q-item-label class="text-weight-bold text-body1">
                {{ form.text }}
              </q-item-label>
              <q-item-label
                v-if="form.is_required"
                caption
                class="text-red-8"
              >
                <q-icon name="mdi-asterisk" size="12px" /> Required to complete the appointment
              </q-item-label>
            </q-item-section>
          </q-item>

          <template v-if="reviewRequirements.length > 0">
            <q-item-label caption class="q-mb-xs q-px-sm">
              Before this task can be ticked off:
            </q-item-label>
            <q-list dense>
              <q-item
                v-for="(req, key) in reviewRequirements"
                :key="'req-' + key"
                class="q-px-sm"
              >
                <q-item-section avatar class="requirement-icon">
                  <q-icon :name="req.icon" color="orange-8" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ req.label }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </template>
          <q-item-label
            v-else
            caption
            class="q-px-sm"
          >
            No requirements — the task can be ticked off at any time.
          </q-item-label>
        </q-step>
      </q-stepper>

      <q-separator />

      <q-toolbar class="editor-footer">
        <q-btn
          v-if="step > 1"
          flat
          no-caps
          color="grey-8"
          icon="mdi-chevron-left"
          label="Back"
          @click="$refs.stepper.previous()"
        />
        <q-btn
          v-else
          flat
          no-caps
          color="grey-8"
          label="Cancel"
          @click="$emit('cancel')"
        />
        <q-space />
        <q-btn
          v-if="step < 3"
          color="primary"
          no-caps
          icon-right="mdi-chevron-right"
          label="Next"
          @click="next"
        />
        <q-btn
          v-else
          color="green"
          icon-right="done"
          no-caps
          :label="saveLabel"
          :loading="loading"
          @click="save"
        />
      </q-toolbar>
    </template>

    <!-- FORM PICKER VIEW: swapped in place of the stepper (no nested dialog) -->
    <template v-else>
      <div class="q-pa-md editor-scroll">
        <q-input
          v-model="formSearch"
          dense
          outlined
          clearable
          autofocus
          placeholder="Search report templates..."
          class="q-mb-sm"
        >
          <template #prepend>
            <q-icon name="search" />
          </template>
        </q-input>

        <q-inner-loading :showing="formsLoading">
          <loading-spinner />
        </q-inner-loading>

        <q-list v-if="!formsLoading">
          <FormGroup
            v-for="formGroup in filteredForms"
            :key="formGroup.refKey"
            :form="formGroup"
            without-timestamp
            @form-click="onFormPicked"
          />

          <q-item v-if="filteredForms.length === 0">
            <q-item-section>
              <q-item-label caption>
                No report templates found
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </div>

      <q-separator />

      <q-toolbar class="editor-footer">
        <q-btn
          flat
          no-caps
          color="grey-8"
          icon="mdi-chevron-left"
          label="Back to task"
          @click="view = 'stepper'"
        />
      </q-toolbar>
    </template>
  </div>
</template>

<script>
import FormGroup from 'components/app/forms/partials/list/group/Group'
import { checkAndUpdateFormGroup } from 'components/app/forms/partials/list/revisionsGroupMixin'
import { doneOptionFor } from 'components/app/checklists/checklistDefinitions'

const emptyForm = () => ({
  text: '',
  is_required: false,
  requires_photo: false,
  requires_invoice: false,
  requires_quotation: false,
  required_form_template_ids: [],
  required_forms: []
})

/*
  Shared multi-step "task" editor, embeddable inside a host dialog (template
  EditDialog swaps it in place of the template view; TaskStepperDialog wraps it
  for the appointment checklist). All sub-views — including the report template
  picker — swap within this component, so the host never stacks dialogs.

  API: call reset(item?, context?) when showing; emits save(payload, context)
  and cancel.
*/
export default {
  components: {
    FormGroup
  },
  props: {
    mode: {
      type: String,
      default: 'appointment'
    },
    loading: {
      type: Boolean,
      default: false
    },
    usedFormIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['save', 'cancel'],
  data () {
    return {
      view: 'stepper',
      step: 1,
      showTextError: false,
      requiresForm: false,
      editing: false,
      context: null,
      form: emptyForm(),
      formSearch: '',
      availableForms: [],
      formsLoading: false,
      formsLoaded: false
    }
  },
  computed: {
    company () {
      return this.$store.getters['company/getCompany']
    },
    invoiceDone () {
      return doneOptionFor('invoice', this.company.checklist_invoice_done_when)
    },
    quotationDone () {
      return doneOptionFor('quotation', this.company.checklist_quotation_done_when)
    },
    reportDone () {
      return doneOptionFor('report', this.company.checklist_report_done_when)
    },
    invoiceExplanation () {
      return this.invoiceDone.explanation
    },
    quotationExplanation () {
      return this.quotationDone.explanation
    },
    reportExplanation () {
      return this.reportDone.explanation
    },
    saveLabel () {
      if (this.editing) {
        return 'Save changes'
      }
      return this.mode === 'template' ? 'Add item' : 'Add task'
    },
    reviewRequirements () {
      const requirements = []
      if (this.form.requires_photo) {
        requirements.push({ icon: 'mdi-camera', label: 'Attach at least one photo' })
      }
      if (this.form.requires_invoice) {
        requirements.push({ icon: 'mdi-file-document-outline', label: this.capitalise(this.invoiceDone.phrase) })
      }
      if (this.form.requires_quotation) {
        requirements.push({ icon: 'mdi-file-document-edit-outline', label: this.capitalise(this.quotationDone.phrase) })
      }
      for (const form of this.form.required_forms) {
        requirements.push({ icon: 'mdi-clipboard-text-outline', label: `${this.capitalise(this.reportDone.phrase)}: ${form.title}` })
      }
      return requirements
    },
    filteredForms () {
      const search = (this.formSearch || '').toLowerCase()

      // The API returns flat template rows — group revisions of the same
      // logical form (shared reference_id) into the shape FormGroup expects
      const groups = {}
      for (const form of this.availableForms) {
        const matches = !search ||
          (form.title || '').toLowerCase().includes(search) ||
          (form.standard || '').toLowerCase().includes(search) ||
          (form.reference_id || '').toLowerCase().includes(search)
        if (matches) {
          checkAndUpdateFormGroup(form, groups)
        }
      }
      return Object.entries(groups).map(([refKey, group]) => ({ refKey, ...group }))
    }
  },
  methods: {
    reset (item = null, context = null) {
      this.editing = !!item
      this.context = context
      this.form = item
        ? {
            text: item.text || '',
            is_required: !!item.is_required,
            requires_photo: !!item.requires_photo,
            requires_invoice: !!item.requires_invoice,
            requires_quotation: !!item.requires_quotation,
            required_form_template_ids: [...(item.required_form_template_ids || [])],
            required_forms: [...(item.required_forms || [])]
          }
        : emptyForm()
      this.requiresForm = this.form.required_form_template_ids.length > 0
      this.showTextError = false
      this.step = 1
      this.view = 'stepper'
      this.formSearch = ''
    },
    next () {
      if (this.step === 1 && (!this.form.text || !this.form.text.trim())) {
        this.showTextError = true
        return
      }
      this.showTextError = false
      this.$refs.stepper.next()
    },
    onToggleForm (val) {
      if (!val) {
        this.form.required_form_template_ids = []
        this.form.required_forms = []
      }
      else if (this.form.required_form_template_ids.length === 0) {
        // Toggling on with nothing chosen yet: prompt the picker immediately.
        this.openFormPicker()
      }
    },
    openFormPicker () {
      this.view = 'form-picker'
      this.loadForms()
    },
    async loadForms () {
      if (this.formsLoaded) {
        return
      }
      this.formsLoading = true
      const { status, payload } = await this.$get('v1/reportingsystem/forms/available')
      this.formsLoading = false
      if (status === 'success') {
        this.availableForms = payload.templates
        this.formsLoaded = true
      }
    },
    onFormPicked (formTemplate) {
      if (this.mode === 'appointment' && this.usedFormIds.includes(formTemplate.id)) {
        this.$q.notify({
          color: 'orange-7',
          message: `A task already exists for "${formTemplate.title}".`
        })
        return
      }
      if (!this.form.required_form_template_ids.includes(formTemplate.id)) {
        this.form.required_form_template_ids.push(formTemplate.id)
        this.form.required_forms.push({ id: formTemplate.id, title: formTemplate.title })
        this.requiresForm = true
      }
      this.view = 'stepper'
    },
    removeForm (formId) {
      this.form.required_form_template_ids = this.form.required_form_template_ids.filter(id => id !== formId)
      this.form.required_forms = this.form.required_forms.filter(f => f.id !== formId)
    },
    capitalise (str) {
      return str ? str.charAt(0).toUpperCase() + str.slice(1) : str
    },
    save () {
      this.$emit('save', {
        text: this.form.text.trim(),
        is_required: this.form.is_required,
        requires_photo: this.form.requires_photo,
        requires_invoice: this.form.requires_invoice,
        requires_quotation: this.form.requires_quotation,
        required_form_template_ids: this.form.required_form_template_ids,
        required_forms: this.form.required_forms
      }, this.context)
    }
  }
}
</script>

<style lang="scss" scoped>
.task-stepper {
  :deep(.q-stepper__step-inner) {
    padding-bottom: 8px;
  }
}

.task-stepper-host {
  // Fill the dialog's flex content area so the stepper scrolls and the footer
  // sits as a flex sibling pinned to the bottom — same structure as System Defaults.
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  .requirement-icon {
    min-width: 36px;
    padding-right: 0;
  }
}

.editor-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.editor-footer {
  flex: none;
}
</style>
